import React from "react";

/**
 * Render a headline as a stack of clipped lines that slide up into place.
 *
 * Each line sits in an `overflow: hidden` wrapper, so its inner span can start
 * fully below the wrapper and travel into view — the type appears to rise out
 * of the page rather than fade onto it. Lines are staggered.
 *
 * The animation itself is CSS (`.line` / `.line__inner` in base.css). It fires
 * when this element — or any ancestor — is stamped `data-revealed="true"` by
 * `useScrollReveal`, or `data-entered="true"` by a component that animates on
 * mount instead (the hero).
 *
 * This component carries its own `data-reveal` by default, so a headline is
 * never left clipped just because the section around it forgot to opt in.
 * Pass `reveal={null}` to rely purely on an ancestor's trigger.
 *
 * @param {Object} props
 * @param {React.ReactNode[]} props.lines     One entry per visual line. Plain
 *   strings are typical, but a node is allowed so a line can be italicised.
 * @param {keyof JSX.IntrinsicElements} [props.as="span"] Element to render.
 * @param {string} [props.className]          Typographic class, e.g. "t-display".
 * @param {number} [props.staggerMs=110]      Delay added per successive line.
 * @param {number} [props.initialDelayMs=0]   Delay before the first line moves.
 * @param {string | null} [props.reveal="fade"] Own reveal mode, or null for none.
 * @returns {JSX.Element}
 */
function MaskedLines({
  lines,
  as: Element = "span",
  className = "",
  staggerMs = 110,
  initialDelayMs = 0,
  reveal = "fade",
}) {
  return (
    <Element className={className} data-reveal={reveal ?? undefined}>
      {lines.map((lineContent, lineIndex) => (
        // Index keys are correct here: the list is static per render and the
        // entries may be nodes rather than strings.
        // eslint-disable-next-line react/no-array-index-key
        <span className="line" key={lineIndex}>
          <span
            className="line__inner"
            style={{ "--line-delay": `${initialDelayMs + lineIndex * staggerMs}ms` }}
          >
            {lineContent}
          </span>
        </span>
      ))}
    </Element>
  );
}

export default MaskedLines;
